// GA4 analytics utility for ContentPipeline observability
// Tracks: pipeline conversion rates, script generation success, Reels publishing events

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

/**
 * Core event tracker — no-ops in non-production or when GA is not configured.
 */
export function trackEvent(
  eventName: string,
  params: Record<string, string | number | boolean> = {}
): void {
  if (typeof window === 'undefined') return;
  if (!GA_ID) return;
  if (typeof window.gtag !== 'function') return;

  window.gtag('event', eventName, {
    ...params,
    app_name: 'ContentPipeline',
  });
}

// ─── Script Generation Events ────────────────────────────────────────────────

/** Fired when the user clicks "Generate Reels Script" */
export function trackScriptGenerationStarted(params: {
  signal_keyword: string;
  signal_platform: string;
  hook_style: string;
  cta_type: string;
}): void {
  trackEvent('script_generation_started', params);
}

/** Fired when OpenAI returns a successfully parsed script */
export function trackScriptGenerationSuccess(params: {
  signal_keyword: string;
  signal_platform: string;
  hook_style: string;
  cta_type: string;
  estimated_duration?: string;
}): void {
  trackEvent('script_generation_success', params);
}

/** Fired when OpenAI returns an error or the response cannot be parsed */
export function trackScriptGenerationFailure(params: {
  signal_keyword: string;
  error_message: string;
}): void {
  trackEvent('script_generation_failure', params);
}

/** Fired when the user copies a generated script */
export function trackScriptCopied(params: {
  signal_keyword: string;
}): void {
  trackEvent('script_copied', params);
}

// ─── Pipeline Conversion Events ───────────────────────────────────────────────

/** Fired when a script moves to "approved" state */
export function trackScriptApproved(params: {
  script_id?: string;
  signal_keyword?: string;
}): void {
  trackEvent('pipeline_script_approved', params);
}

/** Fired when a script enters the render queue */
export function trackScriptRendering(params: {
  script_id?: string;
}): void {
  trackEvent('pipeline_script_rendering', params);
}

/** Fired when a Reel is successfully published to Instagram/Facebook */
export function trackReelPublished(params: {
  platforms: string;          // e.g. "instagram" | "facebook" | "instagram,facebook"
  instagram_id?: string;
  facebook_id?: string;
  caption_length?: number;
}): void {
  trackEvent('reel_published', params);
}

/** Fired when a publish attempt fails */
export function trackReelPublishFailed(params: {
  platforms: string;
  error_message: string;
}): void {
  trackEvent('reel_publish_failed', params);
}
